import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { propEq, find, pathOr } from 'ramda';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { getIncomeTypeOptions, getStateOptions } from '../../App/selectors';
import { updateQualificationChecklist, printIncomeAssetForm } from '../actions';
import { getAllStates, getAllIncomeTypes } from '../../App/actions';
import messages from '../messages.js';
import type { SelectOption } from '../../App/types';
import AssetInformationForm from '../AssetInformationForm';
import IncomeVerificationAlternateForm from '../IncomeVerificationAlternateForm';

import { INITIAL_INCOME_VALUES } from '../IncomeVerificationForm/constants';
import { OHIO_EMPLOYMENT_TYPE_INITIAL_VALUES } from '../IncomeVerificationAlternateForm/constants';
import {
  applyOhioOtherIncomeTypeDefaults,
  applyOhioIncomeVerificationDefaults,
  hasStateSpecificIncomeFormLayout,
} from '../IncomeVerificationAlternateForm/stateRules';
import { STATE_CODES } from '../stateCodes';
import { INITIAL_ASSET_VALUES } from '../AssetInformationForm/constants';
import { getQualificationHistory } from '../../AffordableQualificationHistoryTab/actions';
import { getIsAltForm } from '../utils';
import { getUrlWithSelectedPropertyId } from '../../../utils/navigation-helpers';
import { TAB_KEYS } from '../../ResidentProfile/constants';
import StateAffordableProgramConfigService from '../../../services/stateAffordableProgramConfigService';

type Props = {
  params: Object,
  states: Array<SelectOption>,
  qualificationHistory: Array<Object>,
  userPermissions: Array<Object>,
};

type InjectedProps = {
  intl: any,
  actions: Object,
  history: Object,
  match: Object,
};

export class PriorQualification extends Component<Props & InjectedProps> {
  stateProgramConfigService = new StateAffordableProgramConfigService();

  state = {
    isHotmaActive: false,
  };

  componentDidMount() {
    const {
      qualificationHistory,
      actions: { getAllStates, getQualificationHistory, getAllIncomeTypes },
      params: { applicationId },
    } = this.props;
    if (qualificationHistory.length === 0) {
      getQualificationHistory(applicationId);
    }
    getAllStates();
    getAllIncomeTypes();
    this.fetchHotmaStatus();
  }

  componentDidUpdate(prevProps: Object) {
    const hotmaInputsChanged =
      prevProps.params.qualificationHistoryId !==
        this.props.params.qualificationHistoryId ||
      prevProps.qualificationHistory !== this.props.qualificationHistory ||
      prevProps.programName !== this.props.programName ||
      prevProps.selectedProperty?.physicalState !==
        this.props.selectedProperty?.physicalState;
    if (hotmaInputsChanged) {
      this.fetchHotmaStatus();
    }
  }

  fetchHotmaStatus = async () => {
    const {
      qualificationHistory,
      params: { qualificationHistoryId },
      programName,
      selectedProperty,
    } = this.props;
    const qualification =
      find(propEq('id', qualificationHistoryId))(qualificationHistory) || {};
    const effectiveDate = qualification?.voucherEffectiveDate;
    const state = selectedProperty?.physicalState;

    if (!effectiveDate || !state || !programName) {
      this.setState({ isHotmaActive: false });
      return;
    }

    try {
      const hotmaStatus =
        await this.stateProgramConfigService.determineHotmaStatus({
          programName,
          state,
          effectiveDate,
        });
      this.setState({
        isHotmaActive: hotmaStatus?.hasAdoptedHOTMA ?? false,
      });
    } catch {
      this.setState({ isHotmaActive: false });
    }
  };

  handleIncomeSubmit = (values: Object, annualIncome: string) => {
    const {
      params: { residentId, checklistId },
      actions: { updateQualificationChecklist },
    } = this.props;
    updateQualificationChecklist(
      {
        incomeChecklist: values,
        annualIncome,
      },
      checklistId,
      residentId,
    );
  };

  handleAssetSubmit = (
    values: Object,
    monthlyIncomeFromAssets: string,
    imputedIncome: string,
  ) => {
    const {
      params: { residentId, checklistId },
      actions: { updateQualificationChecklist },
    } = this.props;
    updateQualificationChecklist(
      {
        assetChecklist: values,
        monthlyIncomeFromAssets,
        imputedIncome,
      },
      checklistId,
      residentId,
    );
  };

  goBack = () => {
    const {
      params: { residentId },
    } = this.props;
    this.props.history.push(
      getUrlWithSelectedPropertyId(
        `/resident/${residentId}/?tab=${TAB_KEYS.QUALIFICATION_HISTORY}`,
      ),
    );
  };

  handlePrintIncomeAssetForm = (
    type: string,
    applicantNameFromForm?: string,
  ) => {
    const {
      actions: { printIncomeAssetForm },
      organizationId,
      affordableQualificationId,
      applicantId,
      applicationId,
      propertyId,
      frMembers,
      programName,
    } = this.props;
    const resolvedApplicantName =
      (typeof applicantNameFromForm === 'string'
        ? applicantNameFromForm
        : '') ||
      frMembers[applicantId] ||
      '';
    printIncomeAssetForm(
      organizationId,
      applicationId,
      applicantId,
      propertyId,
      affordableQualificationId,
      type,
      resolvedApplicantName,
      programName,
    );
  };

  render() {
    const {
      states,
      incomeTypes,
      qualificationHistory,
      match: { path },
      params: { qualificationHistoryId, checklistId },
      intl,
      userPermissions,
      flags,
      programName,
      selectedProperty,
    } = this.props;
    const { isHotmaActive } = this.state;
    const qualification =
      find(propEq('id', qualificationHistoryId))(qualificationHistory) || {};
    const qualificationChecklists = pathOr([], ['checklists'], qualification);
    const applicantChecklists = find(propEq('id', checklistId))(
      qualificationChecklists,
    );
    const applicantName = pathOr(
      '',
      ['applicant', 'name'],
      applicantChecklists,
    );
    const incomeChecklist = pathOr(
      {},
      ['incomeChecklist'],
      applicantChecklists,
    );
    const assetChecklist = pathOr({}, ['assetChecklist'], applicantChecklists);
    const qualForm = path.includes('income')
      ? 'incomeVerification'
      : 'assetInformation';

    const isAltForm = getIsAltForm(
      qualForm,
      { incomeChecklist, assetChecklist },
      flags,
    );
    const stateSpecificIncomeLayout = hasStateSpecificIncomeFormLayout(
      selectedProperty?.physicalState,
      programName,
      flags,
      isHotmaActive,
    );
    const isOhioProperty = selectedProperty?.physicalState === STATE_CODES.OHIO;
    // Keep parity with current qualification form routing: always use standard grid,
    // Ohio-specific columns/behavior are applied inside IncomeVerificationAlternateForm via state rules.

    const initialIncomeValuesRaw = {
      ...INITIAL_INCOME_VALUES,
      ...(isOhioProperty && stateSpecificIncomeLayout
        ? OHIO_EMPLOYMENT_TYPE_INITIAL_VALUES
        : {}),
      ...incomeChecklist,
    };
    const initialIncomeValues =
      isOhioProperty && stateSpecificIncomeLayout
        ? applyOhioOtherIncomeTypeDefaults(
            applyOhioIncomeVerificationDefaults(initialIncomeValuesRaw),
          )
        : initialIncomeValuesRaw;
    const initialAssetValues = {
      ...INITIAL_ASSET_VALUES,
      ...assetChecklist,
    };

    const hasCompliancePermission = !!find(
      propEq('scope', 'compliance-approval'),
    )(userPermissions);

    const filteredIncomeTypes = incomeTypes.filter(
      (x) => !x.programFilter || x.programFilter === programName,
    );

    return (
      <div className="container-fluid padtop30 d-flex flex-column h-100 minh-0 overflow-hidden">
        <div className="row flex-shrink-0">
          <div className="col-xs-6 col-sm-12">
            <a className="btn-text" onClick={this.goBack}>
              <i className="et-chevron-left" />
              <FormattedMessage {...messages.goBackToProfile} />
            </a>
          </div>
        </div>
        <div className="d-flex flex-column flex-column-fill overflow-hidden">
          {qualForm === 'incomeVerification' ? (
            <IncomeVerificationAlternateForm
              intl={intl}
              states={states}
              incomeTypes={filteredIncomeTypes}
              onSubmit={this.handleIncomeSubmit}
              initialValues={initialIncomeValues}
              applicantName={applicantName}
              hasCompliancePermission={hasCompliancePermission}
              isPrior={true}
              isAltForm={isAltForm}
              flags={flags}
              handlePrintIncomeAssetForm={(type) =>
                this.handlePrintIncomeAssetForm(type, applicantName)
              }
              selectedProperty={selectedProperty}
              programName={programName}
              isHotmaActive={isHotmaActive}
              wasFormEdited={false}
            />
          ) : (
            <AssetInformationForm
              intl={intl}
              initialValues={initialAssetValues}
              applicantName={applicantName}
              isPrior={true}
              hasCompliancePermission={hasCompliancePermission}
              onSubmit={this.handleAssetSubmit}
              handlePrintIncomeAssetForm={(type) =>
                this.handlePrintIncomeAssetForm(type, applicantName)
              }
              isAltForm={isAltForm}
              flags={flags}
              effectiveDate={qualification?.voucherEffectiveDate}
              programName={programName}
              isHotmaActive={isHotmaActive}
            />
          )}
        </div>
      </div>
    );
  }
}

export const mapStateToProps = (state: Object, ownProps: Object): Object => {
  const {
    qualification: { qualificationHistory },
    app: { currentUser, selectedProperty },
  } = state;
  return {
    params: ownProps.match.params,
    states: getStateOptions(state),
    incomeTypes: getIncomeTypeOptions(state),
    qualificationHistory,
    userPermissions: pathOr([], ['permissions'], currentUser),
    selectedProperty,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    actions: bindActionCreators(
      {
        getAllStates,
        getAllIncomeTypes,
        getQualificationHistory,
        updateQualificationChecklist,
        printIncomeAssetForm,
      },
      dispatch,
    ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withLDConsumer()(injectIntl(PriorQualification)));
